/**
 * What a stage change DECIDES, with nothing that can perform one.
 *
 * A leaf module: its only import is `./stage`, which imports nothing at all. So
 * the two decisions a stage change makes — is this move legal, and is there a
 * database row to write to — can be unit-tested without React, supabase-js or
 * the store. `use-change-stage.ts` composes these with the real writes.
 *
 * Same shape and the same reason as `request-access-order.ts` and `stage.ts`.
 * It is not a stylistic split: `@/lib/errors` pulls in `@sentry/react-native`,
 * which ships untransformable ESM, so anything importing the hook cannot be
 * loaded by jest at all.
 */
import { canTransition, type DealStage } from './stage';

export type StageChangePlan =
  | { ok: true; dealId: string; next: DealStage; rollbackTo: DealStage }
  | { ok: false; dealId: string; reason: 'illegal-transition' | 'server-assigned' };

/**
 * Whether a move may be made, and — when it may — how to undo it.
 *
 * `rollbackTo` is the whole point of this returning a plan rather than a
 * boolean. A rejected server write has to put the card back where it actually
 * WAS, and the only moment that is known for certain is before the optimistic
 * write happens.
 */
export function planStageChange(
  dealId: string,
  from: DealStage,
  to: DealStage,
): StageChangePlan {
  if (!canTransition(from, to)) {
    return { ok: false, dealId, reason: 'illegal-transition' };
  }
  // `diligence` is assigned by the founder-approval RPC, never by a client —
  // that is what makes "an investor cannot put themselves into diligence" a
  // property rather than a convention. `canTransition` still sanctions the move
  // (the RPC's own legality check needs it to), so this has to refuse it
  // separately. No offered action targets `diligence` today, so it is
  // unreachable from the UI — this exists so it STAYS unreachable when someone
  // adds a caller later.
  if (to === 'diligence') {
    return { ok: false, dealId, reason: 'server-assigned' };
  }
  return { ok: true, dealId, next: to, rollbackTo: from };
}

/**
 * Does this deal exist in the database, or only on this device?
 *
 * A locally-analysed deal carries a `deal-*` id and has NO `dealflow` row: it
 * came out of a deck analysis on this client and was never persisted. Writing
 * one to the server fires a doomed update — and now that a failed write rolls
 * the card back, that would bounce the stage and surface an error on a path
 * that works today. A local deal updates the store and stops there; there is
 * nothing to roll back because nothing was written.
 *
 * Pure and tested rather than inlined in the hook because it has been dropped
 * once already while the surrounding write was being rewritten.
 */
export function hasServerRow(dealId: string): boolean {
  return !dealId.startsWith('deal-');
}
